import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { resolveRedirect } from "@/lib/redirects";
import { NotFoundPage } from "@/components/not-found";
import { useEffect } from "react";
import { PageShell, Card } from "@/components/page-shell";
import { Faq, faqJsonLd } from "@/components/faq";
import { RiskSnapshot } from "@/components/risk-snapshot";
import { SourceList } from "@/components/source-list";
import { ShareCard } from "@/components/share-card";
import { CountyFinder } from "@/components/county-finder";
import { CountyMap } from "@/components/county-map";
import { countyNounForCount } from "@/lib/county-label";
import { CDC_STATUS_SOURCE, type StateCountyStatuses } from "@/lib/county-status-text";
import { Checklist, InlineLinks } from "@/components/checklist";
import {
  getState,
  getNearbyStates,
  countyPagesEnabled,
  STATES,
  type StateInfo,
} from "@/lib/states";
import { riskLabel, riskHeadline } from "@/lib/risk";
import { stateFaq } from "@/lib/state-faq";
import { breadcrumbJsonLd, articleJsonLd } from "@/lib/jsonld";
import { stateDescription, stateTitle } from "@/lib/seo";
import { LONE_STAR_TICK, placeEntity } from "@/lib/entities";
import { stateTickTally } from "@/lib/state-tick-tally";
import { absUrl, ogImageUrl } from "@/lib/site";
import { track } from "@/lib/analytics";

export const Route = createFileRoute("/states/$state")({
  // The county list is resolved here, not in the component, and the module holding
  // it is imported dynamically. src/lib/counties.ts is 362KB; shipping it to a
  // browser to render a list of names would be the single largest thing on the page.
  loader: async ({
    params,
  }): Promise<{
    state: StateInfo;
    counties: Array<{ name: string; slug: string; fips: string }>;
    statuses: StateCountyStatuses | null;
  }> => {
    const data = getState(params.state);
    if (!data) {
      // "/states/ga", "/states/New_York": the state they meant, not a dead end.
      const target = resolveRedirect(`/states/${params.state}`);
      if (target) throw redirect({ href: target, statusCode: 301 });
      throw notFound();
    }
    if (!countyPagesEnabled(data)) return { state: data, counties: [], statuses: null };
    const [{ getCountiesByState }, { statusesForState }] = await Promise.all([
      import("@/lib/reports"),
      import("@/lib/county-status"),
    ]);
    const counties = [...getCountiesByState(data.abbr)]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((c) => ({ name: c.name, slug: c.slug, fips: c.fips }));
    // Two short FIPS lists for the county map, not CDC's national file.
    const statuses = statusesForState(counties.map((c) => c.fips));
    return { state: data, counties, statuses };
  },
  head: ({ loaderData, params, matches, match }) => {
    // Belt and braces. /states/$state/$county used to nest under this route, which
    // meant this head() ran on county pages too and gave every one of them two
    // <link rel=canonical> tags plus a second FAQPage, Article and BreadcrumbList
    // describing the parent state. The county route no longer nests (its file is
    // states.$state_.$county.tsx — the trailing underscore opts out), so this guard
    // is no longer load-bearing; it stays because re-nesting is one rename away.
    if (matches[matches.length - 1]?.routeId !== match.routeId) return {};
    const s = loaderData?.state;
    const name = s?.name ?? params.state;
    const peak = s?.peakSeason ?? "";
    const path = `/states/${params.state}`;
    const ogImage = ogImageUrl({
      title: s ? riskHeadline(s.risk, s.name) : `Lone Star ticks in ${name}`,
      subtitle: peak ? `Peak season: ${peak}` : "Tick & alpha-gal awareness",
      badge: s ? riskLabel[s.risk] : undefined,
      level: s?.risk,
      kind: "state",
      state: s?.slug,
    });
    // CDC's county tally leads the snippet, so the result itself answers the search.
    const title = s ? stateTitle(s.name, s.risk) : `Lone Star Ticks in ${name}`;
    const description = s
      ? stateDescription({
          stateName: s.name,
          level: s.risk,
          peakSeason: peak,
          counts: stateTickTally(s.abbr),
          nouns: countyNounForCount(s.abbr),
        })
      : "";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: `Lone Star Ticks in ${name}` },
        { property: "og:description", content: description },
        { property: "og:url", content: absUrl(path) },
        { property: "og:type", content: "article" },
        { property: "og:image", content: ogImage },
        { name: "twitter:title", content: `Lone Star Ticks in ${name}` },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: absUrl(path) }],
      scripts: s
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify(faqJsonLd(stateFaq(s))),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify(
                articleJsonLd({
                  headline: `Lone Star Ticks in ${name}`,
                  description: s.summary,
                  path,
                  datePublished: s.lastReviewed,
                  section: "Tick awareness",
                  image: ogImage,
                  about: [LONE_STAR_TICK, placeEntity(name)],
                }),
              ),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify(
                breadcrumbJsonLd([
                  { name: "Home", path: "/" },
                  { name: "States", path: "/states" },
                  { name, path },
                ]),
              ),
            },
          ]
        : [],
    };
  },
  errorComponent: ({ error }) => (
    <PageShell title="Couldn't load state">
      <p className="text-sm text-muted-foreground">
        {error instanceof Error ? error.message : String(error)}
      </p>

    </PageShell>
  ),
  notFoundComponent: () => <NotFoundPage kind="state" />,
  component: StatePage,
});

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function StatePage() {
  const { state: s, counties, statuses } = Route.useLoaderData();
  const faqItems = stateFaq(s);
  const nearby = getNearbyStates(s);

  useEffect(() => {
    track("state_page_viewed", { state: s.name, slug: s.slug, risk: s.risk });
  }, [s.name, s.slug, s.risk]);

  // Answer, then what to do, then detail. V3 answered "are there ticks here?" three
  // times (snapshot, a prose card, the FAQ) and put a 254-name county grid, six source
  // cards and a signup form between a reader and the end of the page.
  return (
    <PageShell
      crumbs={[
        { name: "Home", path: "/" },
        { name: "States", path: "/states" },
        { name: s.name, path: `/states/${s.slug}` },
      ]}
      title={`Lone Star ticks in ${s.name}`}
    >
      <RiskSnapshot state={s} locator />

      <Card>
        <h2 className="font-display text-[1.75rem] font-medium leading-tight text-foreground">
          If you find one on you
        </h2>
        <p className="measure mt-2 text-[0.9375rem] leading-relaxed text-muted-foreground">
          Take it out with fine-tipped tweezers, pulling straight up, and write down the date. The
          step-by-step guide tells you what to watch for, and for how long.
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
          <Link
            to="/tick-bite-tool"
            className="inline-flex rounded-sm bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary-dark"
          >
            What to do after a bite
          </Link>
          <Link to="/tick-identification" className="link text-sm font-semibold">
            Identify the tick
          </Link>
        </div>
      </Card>

      <Card>
        <h2 className="font-display text-[1.75rem] font-medium leading-tight text-foreground">
          How to avoid bites in {s.name}
        </h2>
        <Checklist items={s.preventionNotes} className="mt-5" />
        <Link to="/prevention" className="link mt-4 inline-block text-sm font-semibold">
          The full prevention guide
        </Link>
      </Card>

      {counties.length > 0 && (
        <CountyFinder
          stateName={s.name}
          stateSlug={s.slug}
          stateAbbr={s.abbr}
          counties={counties}
          map={
            statuses && (
              <CountyMap
                stateAbbr={s.abbr}
                stateSlug={s.slug}
                stateName={s.name}
                nouns={countyNounForCount(s.abbr)}
                lists={statuses}
                year={CDC_STATUS_SOURCE.year}
              />
            )
          }
        />
      )}

      <Card>
        <h2 className="font-display text-[1.75rem] font-medium leading-tight text-foreground">
          Common questions
        </h2>
        <div className="mt-5">
          <Faq items={faqItems} />
        </div>
      </Card>

      <SourceList
        ids={s.sourceIds}
        title="Where this comes from"
        collapsible
        footnote={`Last reviewed ${formatDate(s.lastReviewed)}`}
      />

      <nav aria-labelledby="nearby-states">
        <h2 id="nearby-states" className="label-caps text-muted-foreground">
          Nearby states
        </h2>
        <InlineLinks className="mt-3">
          {nearby.map((x) => (
            <li key={x.slug}>
              <Link to="/states/$state" params={{ state: x.slug }} className="link">
                {x.name}
              </Link>
            </li>
          ))}
          <li>
            <Link to="/states" className="link font-semibold">
              All {STATES.length}
            </Link>
          </li>
        </InlineLinks>
      </nav>


      <ShareCard
        title={riskHeadline(s.risk, s.name)}
        subtitle={`Peak season: ${s.peakSeason}`}
        level={s.risk}
        badge={riskLabel[s.risk]}
        kind="state"
        state={s.slug}
        summaryText={`Lone Star ticks in ${s.name}: ${riskLabel[s.risk]}\nPeak season: ${s.peakSeason}\n${s.summary}\nhttps://lonestarmap.com/states/${s.slug}`}
        sharePath={`/states/${s.slug}`}
        shareText={`Know someone in ${s.name} who hikes, hunts, owns a dog or works outdoors? Where Lone Star ticks live, and what to do about them:`}
      />

      <p className="text-xs leading-relaxed text-muted-foreground">
        Educational information only, not medical advice. If someone is struggling to breathe or
        having a severe reaction, call 911. Read the{" "}
        <Link to="/disclaimer" className="link">
          disclaimer
        </Link>
        .
      </p>
      <p className="print-only text-xs">
        Printed from lonestarmap.com/states/{s.slug}. Check the page for the latest version.
      </p>
    </PageShell>
  );
}
